/**
 * Bulk User Import Modal
 *
 * Multi-step wizard for importing users via CSV:
 * 1. Download template
 * 2. Upload and validate CSV
 * 3. Review validation results
 * 4. Execute import
 * 5. Show results with temporary passwords
 */

import { useState, useRef, useMemo } from 'react';
import {
  Upload,
  Download,
  FileText,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  Users,
  X
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import toast from 'react-hot-toast';
import { ImportPreviewTable } from './ImportPreviewTable';
import type { ParsedUserRow } from './ImportPreviewTable';

interface BulkUserImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  onImportComplete?: () => void;
}

type ImportStep = 'upload' | 'validating' | 'review' | 'importing' | 'complete';

interface ValidationError {
  row: number;
  errors: string[];
  email?: string;
  data?: Record<string, string | undefined>;
}

interface ValidationResult {
  valid: boolean;
  totalRows: number;
  validRows: number;
  invalidRows: number;
  errors: ValidationError[];
  parsedData?: Record<string, string | undefined>[];
}

interface ImportResult {
  success: boolean;
  totalProcessed: number;
  invitationsSent: number;
  invitationsFailed: number;
  skipped: number;
  invitedUsers: { email: string; status: 'invited' | 'skipped' | 'failed'; reason?: string }[];
  errors: { row: number; email: string; error: string }[];
}

export function BulkUserImportModal({
  isOpen,
  onClose,
  organizationId,
  organizationName,
  onImportComplete
}: BulkUserImportModalProps) {
  const [step, setStep] = useState<ImportStep>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<ValidationResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importMode, setImportMode] = useState<'abort_on_error' | 'continue_on_error'>('abort_on_error');
  const [isLoading, setIsLoading] = useState(false);
  const [previewPage, setPreviewPage] = useState(1);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const previewRows = useMemo<ParsedUserRow[]>(() => {
    if (!validationResult) return [];

    // parsedData only contains valid rows — we need to reconstruct all rows
    // by merging valid rows (parsedData) and invalid rows (errors[].data)
    const allRows: ParsedUserRow[] = [];

    // Valid rows: errors array has row numbers (1-indexed, header=1, first data=2)
    // parsedData rows are ordered, row numbers are 2..N for non-errored rows
    // We rebuild by going through ALL records in order using error data for invalid rows
    // and parsedData for valid rows.
    const errorByRow = new Map(validationResult.errors.map((e) => [e.row, e]));
    const invalidRowNumbers = new Set(validationResult.errors.map((e) => e.row));
    let validDataIndex = 0;

    for (let dataRowIndex = 1; dataRowIndex <= validationResult.totalRows; dataRowIndex++) {
      const rowNumber = dataRowIndex + 1; // header is row 1, first data row is row 2

      if (invalidRowNumbers.has(rowNumber)) {
        const errorEntry = errorByRow.get(rowNumber);
        const raw = errorEntry ?? { errors: [] };
        const rawData = raw.data ?? {};
        allRows.push({
          rowIndex: dataRowIndex,
          email: rawData.email ?? '',
          firstName: rawData.firstName ?? '',
          lastName: rawData.lastName ?? '',
          role: rawData.role ?? '',
          externalId: rawData.externalId,
          isValid: false,
          errors: raw.errors,
        });
      } else {
        const row = validationResult.parsedData?.[validDataIndex];
        // parsedData contains only valid rows in CSV order; advance the pointer only when we find a non-error row.
        if (row) validDataIndex++;
        allRows.push({
          rowIndex: dataRowIndex,
          email: row?.email ?? '',
          firstName: row?.firstName ?? '',
          lastName: row?.lastName ?? '',
          role: row?.role ?? '',
          externalId: row?.externalId,
          isValid: true,
          errors: [],
        });
      }
    }

    return allRows;
  }, [validationResult]);

  const resetState = () => {
    setStep('upload');
    setSelectedFile(null);
    setValidationResult(null);
    setImportResult(null);
    setImportMode('abort_on_error');
    setIsLoading(false);
    setPreviewPage(1);
  };

  const handleClose = () => {
    resetState();
    onClose();
  };

  const downloadTemplate = async () => {
    setIsLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/users/import/template?organizationId=${organizationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to download template');
      }

      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `user-import-template-${organizationName.replace(/\s+/g, '-').toLowerCase()}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to download template');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const validateFile = async () => {
    if (!selectedFile) return;

    setStep('validating');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('organizationId', organizationId);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/users/import/validate`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Validation failed');
      }

      setValidationResult(data.data);
      setStep('review');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Validation failed');
      setStep('upload');
    } finally {
      setIsLoading(false);
    }
  };

  const executeImport = async () => {
    if (!selectedFile) return;

    setStep('importing');
    setIsLoading(true);

    try {
      const token = localStorage.getItem('accessToken');
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('organizationId', organizationId);
      formData.append('mode', importMode);

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/api/v1/users/import/execute`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Import failed');
      }

      setImportResult(data.data);
      setStep('complete');

      if (data.data.invitationsSent > 0) {
        onImportComplete?.();
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Import failed');
      setStep('review');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 mb-2">
            <div className="p-2 bg-purple-100 rounded-full">
              <Users className="h-6 w-6 text-purple-600" />
            </div>
            <DialogTitle>Bulk User Import</DialogTitle>
          </div>
          <DialogDescription>
            Import multiple users to {organizationName} via CSV file
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex justify-center gap-2 py-4">
          {(['upload', 'review', 'complete'] as const).map((s, i) => (
            <div
              key={s}
              className={`w-3 h-3 rounded-full ${
                step === s || (step === 'validating' && s === 'upload') || (step === 'importing' && s === 'review')
                  ? 'bg-purple-600'
                  : ['upload', 'validating'].includes(step) && i > 0
                  ? 'bg-gray-200'
                  : step === 'complete' || (step === 'review' && i < 2) || (step === 'importing' && i < 2)
                  ? 'bg-green-500'
                  : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step: Upload */}
        {(step === 'upload' || step === 'validating') && (
          <div className="space-y-6">
            {/* Download template */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Step 1: Download Template</h4>
              <p className="text-sm text-gray-600 mb-3">
                Download the CSV template with all required fields and examples.
              </p>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                disabled={isLoading}
              >
                <Download className="mr-2 h-4 w-4" />
                Download Template
              </Button>
            </div>

            {/* CSV columns info panel */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm">
              <h4 className="font-medium text-blue-900 mb-2">CSV Column Reference</h4>
              <div className="space-y-1 text-blue-800">
                <p><span className="font-medium">Required:</span> email, firstName, lastName, role</p>
                <p><span className="font-medium">Optional:</span> title, department, phone, medicalLicense, specialty, deaNumber, licenseExpiration</p>
                <p>
                  <span className="font-medium">Note:</span>{' '}
                  <span className="font-medium text-blue-900">externalId</span>
                  {' '}— Must be unique within your organization. Used to link users to the external EHR system.
                </p>
              </div>
            </div>

            {/* Upload file */}
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium mb-2">Step 2: Upload CSV</h4>
              <p className="text-sm text-gray-600 mb-3">
                Fill in the template and upload it. Maximum 1000 users per import.
              </p>

              <input
                type="file"
                accept=".csv"
                onChange={handleFileSelect}
                ref={fileInputRef}
                className="hidden"
              />

              {!selectedFile ? (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="w-full p-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-400 hover:bg-purple-50 transition-colors"
                >
                  <Upload className="mx-auto h-10 w-10 text-gray-400 mb-2" />
                  <p className="text-sm text-gray-600">
                    Click to upload or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    CSV file up to 5MB
                  </p>
                </button>
              ) : (
                <div className="flex items-center justify-between p-3 bg-white border rounded-lg">
                  <div className="flex items-center gap-3">
                    <FileText className="h-8 w-8 text-purple-600" />
                    <div>
                      <p className="font-medium">{selectedFile.name}</p>
                      <p className="text-sm text-gray-500">
                        {(selectedFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedFile(null);
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="h-5 w-5 text-gray-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Validate button */}
            <Button
              onClick={validateFile}
              disabled={!selectedFile || isLoading}
              className="w-full"
            >
              {step === 'validating' ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Validating...
                </>
              ) : (
                'Validate CSV'
              )}
            </Button>
          </div>
        )}

        {/* Step: Review */}
        {step === 'review' && validationResult && (
          <div className="space-y-4">
            {/* Validation summary */}
            <div className={`p-4 rounded-lg ${
              validationResult.valid
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-start gap-3">
                {validationResult.valid ? (
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5" />
                )}
                <div>
                  <h4 className={`font-medium ${
                    validationResult.valid ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    {validationResult.valid
                      ? 'Validation Passed'
                      : `${validationResult.invalidRows} row(s) have errors`}
                  </h4>
                  <p className={`text-sm mt-1 ${
                    validationResult.valid ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    {validationResult.validRows} of {validationResult.totalRows} rows are valid
                  </p>
                </div>
              </div>
            </div>

            {/* Preview table — all rows */}
            {previewRows.length > 0 && (
              <ImportPreviewTable
                rows={previewRows}
                page={previewPage}
                pageSize={20}
                onPageChange={setPreviewPage}
                importMode={importMode}
              />
            )}

            {/* Import mode selection */}
            {!validationResult.valid && validationResult.validRows > 0 && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-2">Import Mode</h4>
                <div className="space-y-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="importMode"
                      checked={importMode === 'continue_on_error'}
                      onChange={() => setImportMode('continue_on_error')}
                      className="text-purple-600"
                    />
                    <span className="text-sm">
                      Import valid rows only ({validationResult.validRows} users)
                    </span>
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="importMode"
                      checked={importMode === 'abort_on_error'}
                      onChange={() => setImportMode('abort_on_error')}
                      className="text-purple-600"
                    />
                    <span className="text-sm">
                      Fix errors first (recommended)
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => {
                  setStep('upload');
                  setValidationResult(null);
                  setPreviewPage(1);
                }}
                className="flex-1"
              >
                Back
              </Button>
              <Button
                onClick={executeImport}
                disabled={
                  isLoading ||
                  validationResult.validRows === 0 ||
                  (!validationResult.valid && importMode === 'abort_on_error')
                }
                className="flex-1"
              >
                {step === 'importing' ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Importing...
                  </>
                ) : (
                  `Import ${validationResult.validRows} Users`
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <div className="flex flex-col items-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-purple-600 mb-4" />
            <p className="text-lg font-medium">Importing users...</p>
            <p className="text-sm text-gray-500">This may take a moment</p>
          </div>
        )}

        {/* Step: Complete */}
        {step === 'complete' && importResult && (
          <div className="space-y-4">
            {/* Summary */}
            <div className={`p-4 rounded-lg ${
              importResult.invitationsFailed === 0
                ? 'bg-green-50 border border-green-200'
                : 'bg-amber-50 border border-amber-200'
            }`}>
              <div className="flex items-start gap-3">
                {importResult.invitationsFailed === 0 ? (
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5" />
                ) : (
                  <AlertTriangle className="h-6 w-6 text-amber-600 mt-0.5" />
                )}
                <div>
                  <h4 className={`font-medium ${
                    importResult.invitationsFailed === 0 ? 'text-green-800' : 'text-amber-800'
                  }`}>
                    Import Complete
                  </h4>
                  <p className={`text-sm mt-1 ${
                    importResult.invitationsFailed === 0 ? 'text-green-700' : 'text-amber-700'
                  }`}>
                    {importResult.invitationsSent} invitation{importResult.invitationsSent !== 1 ? 's' : ''} sent
                    {importResult.skipped > 0 && `, ${importResult.skipped} skipped`}
                    {importResult.invitationsFailed > 0 && `, ${importResult.invitationsFailed} failed`}
                  </p>
                </div>
              </div>
            </div>

            {/* Info about next steps */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
              Users will receive an email with a link to set up their account and create their own password.
            </div>

            {/* Per-user result table */}
            {importResult.invitedUsers && importResult.invitedUsers.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between bg-gray-50 px-4 py-2 border-b">
                  <h4 className="font-medium">Import Results</h4>
                  {importResult.invitedUsers.some((u) => u.status === 'failed') && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const failed = importResult.invitedUsers.filter(
                          (u) => u.status === 'failed'
                        );
                        if (!failed.length) return;
                        const header = 'email,reason\n';
                        const csvRows = failed
                          .map((u) => `"${u.email}","${u.reason ?? ''}"`)
                          .join('\n');
                        const blob = new Blob([header + csvRows], { type: 'text/csv' });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'failed-imports.csv';
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                        URL.revokeObjectURL(url);
                      }}
                      className="text-xs"
                    >
                      <Download className="mr-1 h-3 w-3" />
                      Download failed as CSV
                    </Button>
                  )}
                </div>
                <div className="max-h-64 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Email</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Status</th>
                        <th className="px-4 py-2 text-left font-medium text-gray-700">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importResult.invitedUsers.map((user, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-4 py-2 font-mono text-xs">{user.email}</td>
                          <td className="px-4 py-2">
                            {user.status === 'invited' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Enviada
                              </span>
                            )}
                            {user.status === 'skipped' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                                Ya existía
                              </span>
                            )}
                            {user.status === 'failed' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                Falló
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500">
                            {user.reason ?? '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Done button */}
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
